import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ExternalLink, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ApiErrorDisplay } from '@/components/features/error';
import { MerchantInfo } from '@/components/features/merchants/merchant-info';
import { MerchantWebhookCard } from '@/components/features/merchants/merchant-webhook-card';
import { MerchantForm } from '@/components/features/merchants/merchant-form';
import { useMerchant, useUpdateMerchant, useDeleteMerchant } from '@/hooks/use-merchants';
import { useAuth } from '@/hooks/use-auth';
import { ApiError } from '@/lib/api/client';
import type { CreateMerchantParams, UpdateMerchantParams } from '@/types/merchant';

export function MerchantDetailPage() {
  const { t } = useTranslation('merchants');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === 'platform_admin';
  const isMerchantAdmin = user?.role === 'merchant_admin';

  const [isEditing, setIsEditing] = useState(false);

  const { data: merchant, isLoading, error, refetch } = useMerchant(id);
  const updateMutation = useUpdateMerchant(id ?? '');
  const deleteMutation = useDeleteMerchant(id ?? '');

  if (!id) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">Missing merchant ID</p>
        <Button variant="link" onClick={() => navigate('/merchants')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!merchant) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('list.empty')}</p>
        <Button variant="link" onClick={() => navigate('/merchants')}>
          {t('detail.back')}
        </Button>
      </div>
    );
  }

  const handleUpdate = async (data: CreateMerchantParams | UpdateMerchantParams) => {
    try {
      await updateMutation.mutateAsync(data as UpdateMerchantParams);
      toast.success(t('actions.updateSuccess'));
      setIsEditing(false);
    } catch {
      // Error handled by form via updateMutation.error
    }
  };

  const handleDeactivate = async () => {
    try {
      await deleteMutation.mutateAsync();
      toast.success(t('actions.deactivateSuccess'));
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'MERCHANT_HAS_SERVICES':
            toast.error(t('actions.hasServicesError'));
            break;
          case 'MERCHANT_HAS_IQ_SERVICES':
            toast.error(t('actions.hasIqServicesError'));
            break;
          default:
            toast.error(err.message);
        }
      } else {
        toast.error(String(err));
      }
    }
  };

  const handleReactivate = async () => {
    try {
      await updateMutation.mutateAsync({ status: 'active' });
      toast.success(t('actions.reactivateSuccess'));
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error(String(err));
      }
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('detail.title')}</h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{merchant.merchantId}</p>
        </div>
        <div className="flex gap-2">
          {isPlatformAdmin && !isEditing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                {t('actions.edit')}
              </Button>

              {merchant.status === 'active' && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" loading={deleteMutation.isPending}>
                      {t('actions.deactivate')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('actions.confirmDeactivate.title')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('actions.confirmDeactivate.description')}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t('actions.confirmDeactivate.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeactivate}>
                        {t('actions.confirmDeactivate.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {(merchant.status === 'suspended' || merchant.status === 'deactivated') && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleReactivate}
                  loading={updateMutation.isPending}
                >
                  {t('actions.activate')}
                </Button>
              )}
            </>
          )}

          <Button variant="outline" size="sm" onClick={() => navigate('/merchants')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('detail.back')}
          </Button>
        </div>
      </div>

      {isEditing ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('form.editTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <MerchantForm
              mode="edit"
              initialData={merchant}
              onSubmit={handleUpdate}
              onCancel={() => setIsEditing(false)}
              isLoading={updateMutation.isPending}
              error={updateMutation.error}
            />
          </CardContent>
        </Card>
      ) : (
        <MerchantInfo merchant={merchant} />
      )}

      {/* Quick Links — always visible */}
      {!isEditing && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base">{t('detail.quickLinks')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/services?merchantId=${merchant.merchantId}`)}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('detail.viewServices')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/transactions?merchantId=${merchant.merchantId}`)}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('detail.viewTransactions')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/webhooks/config?merchantId=${merchant.merchantId}`)}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                {t('detail.viewWebhookConfig')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Webhook Config Card — platform_admin and merchant_admin only */}
      {!isEditing && (isPlatformAdmin || isMerchantAdmin) && (
        <MerchantWebhookCard merchantId={merchant.merchantId} />
      )}
    </div>
  );
}
